import { ReactNode } from 'react'
import { TFunction } from 'i18next'
import { CharacterClassIcon } from 'app/frontend/base/components'
import BlizzardIcon from 'app/frontend/base/components/BlizzardIcon'
import CharacterRoleIcon from 'app/frontend/base/components/CharacterRoleIcon'
import { getAllClasses } from 'app/frontend/base/services/characterValues'
import {
  RaidPlanAssignmentEntity,
  RaidPlanAssignmentEntity as AssignmentEntity,
} from 'app/frontend/types/custom/RaidPlan'
import { CharacterClass } from 'app/frontend/types/custom'

export type AssignmentPaletteEntity = {
  id: string
  entity: RaidPlanAssignmentEntity
  label: ReactNode
  category:
    | 'collection'
    | 'group'
    | 'specific_character'
    | 'by_role'
    | 'by_class'
  specificCharacterFilter?: {
    role: 'tank' | 'heal' | 'melee' | 'ranged' | null
    raidVisible: boolean
    forAlts: boolean
    teamRankId: number | null
    teamRankName: string
  }
}

type BuildAssignmentPaletteEntitiesInput = {
  t: TFunction
  members: wowaudit.TeamMembership[]
  specializationById: Record<
    number,
    {
      name: string
      icon: string
      classId: number
      className: string
    }
  >
  classColor: (classId: number) => string
}

const toSpecificCharacterFilterRole = (
  role: string | null | undefined,
): 'tank' | 'heal' | 'melee' | 'ranged' | null => {
  if (role === 'tank') return 'tank'
  if (role === 'heal') return 'heal'
  if (role === 'melee') return 'melee'
  if (role === 'ranged') return 'ranged'
  return null
}

export const buildAssignmentPaletteEntities = ({
  t,
  members,
  specializationById,
  classColor,
}: BuildAssignmentPaletteEntitiesInput): AssignmentPaletteEntity[] => {
  const memberRoleOrder: Record<string, number> = {
    tank: 0,
    heal: 1,
    healer: 1,
    melee: 2,
    ranged: 3,
    dps: 4,
    damage: 4,
  }
  const roleLabel = (
    label: string,
    role: 'tank' | 'heal' | 'damage' | 'ranged' | 'melee',
  ) => (
    <span className='inline-flex items-center gap-1.5 min-w-0'>
      <CharacterRoleIcon role={role} size='small' className='shrink-0' />
      <span className='truncate'>{label}</span>
    </span>
  )
  const memberEntities = members
    .sort((left, right) => {
      const leftRoleOrder =
        memberRoleOrder[left.role] ?? Number.MAX_SAFE_INTEGER
      const rightRoleOrder =
        memberRoleOrder[right.role] ?? Number.MAX_SAFE_INTEGER
      if (leftRoleOrder !== rightRoleOrder) {
        return leftRoleOrder - rightRoleOrder
      }
      return left.characterReference.name.localeCompare(
        right.characterReference.name,
      )
    })
    .map((membership) => {
      const character = membership.characterReference
      const specialization = membership.specId
        ? specializationById[membership.specId]
        : undefined
      const roleIcon = membership.role === 'heal' ? 'heal' : membership.role
      const memberLabel = character.name
      const characterClassId = character.classId
      return {
        id: `entity:member:${character.id}`,
        entity: {
          kind: 'member',
          character_id: character.id,
          spec_id: membership.specId,
          role: membership.role,
          label: memberLabel,
        } as Extract<AssignmentEntity, { kind: 'member' }>,
        label: (
          <span className='inline-flex items-center gap-1.5 min-w-0'>
            {specialization?.icon ? (
              <BlizzardIcon
                iconName={specialization.icon}
                size='small'
                className='shrink-0'
              />
            ) : (
              <CharacterRoleIcon
                role={roleIcon}
                size='small'
                className='shrink-0'
              />
            )}
            <span
              className='truncate'
              style={
                characterClassId
                  ? { color: classColor(characterClassId) }
                  : undefined
              }
            >
              {memberLabel}
            </span>
          </span>
        ),
        category: 'specific_character' as const,
        specificCharacterFilter: {
          role: toSpecificCharacterFilterRole(membership.role),
          raidVisible: Boolean(membership.teamRank?.raid_visibility),
          forAlts: Boolean(membership.teamRank?.for_alts),
          teamRankId: membership.teamRank?.id ?? null,
          teamRankName: membership.teamRank?.name ?? 'Unknown',
        },
      }
    })
  const classEntities = Object.entries(getAllClasses())
    .map(([classId, className]) => ({
      classId: Number(classId),
      className,
    }))
    .filter((entry) => Number.isFinite(entry.classId))
    .sort((left, right) => left.className.localeCompare(right.className))
    .map((entry) => ({
      id: `entity:by-class:${entry.classId}`,
      entity: {
        kind: 'by_class' as const,
        class_id: entry.classId,
      },
      label: (
        <span
          className='flex items-center gap-1.5 min-w-0'
          style={{ color: classColor(entry.classId) }}
        >
          <CharacterClassIcon
            characterClass={entry.className as CharacterClass}
            size='small'
          />
          <span className='truncate'>{entry.className}</span>
        </span>
      ),
      category: 'by_class' as const,
    }))

  return [
    {
      id: 'entity:collection:everyone',
      entity: { kind: 'collection', collection: 'everyone' },
      label: t('dashboard.team.plans.assignments.entities.collection.everyone'),
      category: 'collection',
    },
    {
      id: 'entity:collection:tanks',
      entity: { kind: 'collection', collection: 'tanks' },
      label: roleLabel(
        t('dashboard.team.plans.assignments.entities.collection.tanks'),
        'tank',
      ),
      category: 'collection',
    },
    {
      id: 'entity:collection:healers',
      entity: { kind: 'collection', collection: 'healers' },
      label: roleLabel(
        t('dashboard.team.plans.assignments.entities.collection.healers'),
        'heal',
      ),
      category: 'collection',
    },
    {
      id: 'entity:collection:dps',
      entity: { kind: 'collection', collection: 'dps' },
      label: roleLabel(
        t('dashboard.team.plans.assignments.entities.collection.dps'),
        'damage',
      ),
      category: 'collection',
    },
    {
      id: 'entity:collection:ranged',
      entity: { kind: 'collection', collection: 'ranged' },
      label: roleLabel(
        t('dashboard.team.plans.assignments.entities.collection.ranged'),
        'ranged',
      ),
      category: 'collection',
    },
    {
      id: 'entity:collection:melee',
      entity: { kind: 'collection', collection: 'melee' },
      label: roleLabel(
        t('dashboard.team.plans.assignments.entities.collection.melee'),
        'melee',
      ),
      category: 'collection',
    },
    ...Array.from({ length: 8 }, (_, index) => ({
      id: `entity:group:${index + 1}`,
      entity: {
        kind: 'group' as const,
        group: (index + 1) as 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8,
      },
      label: t('dashboard.team.plans.assignments.entities.group', {
        count: index + 1,
      }),
      category: 'group' as const,
    })),
    ...memberEntities,
    {
      id: 'entity:by-role:anyone',
      entity: { kind: 'by_role', role: 'anyone' },
      label: t('dashboard.team.plans.assignments.entities.by-role.anyone'),
      category: 'by_role',
    },
    {
      id: 'entity:by-role:tank',
      entity: { kind: 'by_role', role: 'tank' },
      label: roleLabel(
        t('dashboard.team.plans.assignments.entities.by-role.tank'),
        'tank',
      ),
      category: 'by_role',
    },
    {
      id: 'entity:by-role:healer',
      entity: { kind: 'by_role', role: 'healer' },
      label: roleLabel(
        t('dashboard.team.plans.assignments.entities.by-role.healer'),
        'heal',
      ),
      category: 'by_role',
    },
    {
      id: 'entity:by-role:dps',
      entity: { kind: 'by_role', role: 'dps' },
      label: roleLabel(
        t('dashboard.team.plans.assignments.entities.by-role.dps'),
        'damage',
      ),
      category: 'by_role',
    },
    {
      id: 'entity:by-role:ranged',
      entity: { kind: 'by_role', role: 'ranged' },
      label: roleLabel(
        t('dashboard.team.plans.assignments.entities.by-role.ranged'),
        'ranged',
      ),
      category: 'by_role',
    },
    {
      id: 'entity:by-role:melee',
      entity: { kind: 'by_role', role: 'melee' },
      label: roleLabel(
        t('dashboard.team.plans.assignments.entities.by-role.melee'),
        'melee',
      ),
      category: 'by_role',
    },
    ...classEntities,
  ]
}
